import { Temporal } from "temporal-polyfill";
import { TimeZone } from "@api/shared-runtime/timezone";
import { formatDate } from "../../lib/format-date";
import type {
  DisciplinaryAssessment,
  VerificationAssessmentItem,
  VerificationCheck,
  VerificationResult,
} from "./types";

type Attachment = VerificationCheck["attachments"][number];
type Facts = VerificationResult["facts"];

/** One problem a reviewer must resolve, stated plainly with its evidence. */
export type LicenseIssue = { title: string; details: string[] };

export const CURRENT_LICENSE_STATUS = "current";
const EXPIRED_LICENSE_STATUS = "expired";
/** Normalized license statuses that neither confirm nor contradict validity. */
export const UNCONFIRMED_LICENSE_STATUSES = new Set(["pending", "unknown"]);
/** Mirrors the API's invalid statuses; an expired license is reported apart. */
const INVALID_LICENSE_STATUSES = new Set([
  "inactive",
  "delinquent",
  "cancelled",
  "revoked",
  "suspended",
  "probation",
  "deceased",
]);
const IDENTITY_ISSUES: Partial<
  Record<VerificationResult["identityMatch"], string>
> = {
  "potential-match": "Possible identity match",
  "not-matched": "Identity did not match",
  "insufficient-information": "Not enough information to match identity",
};
/** Each outcome issue with what it means, so no issue is a bare title. */
const OUTCOME_ISSUES: Partial<
  Record<VerificationResult["outcome"], LicenseIssue>
> = {
  "no-record-found": {
    title: "No license record found",
    details: ["The source returned no record for this license."],
  },
  "no-matches": {
    title: "No matching records",
    details: ["The source's search returned no records for this provider."],
  },
  "possible-match": {
    title: "Possible match",
    details: ["The source returned a record that may belong to this provider."],
  },
  "adverse-record": {
    title: "Adverse record reported",
    details: ["The source reports an action on this record. Review it below."],
  },
  incomplete: {
    title: "Incomplete result",
    details: ["The source returned only part of the record."],
  },
};
const CONFIRM_IDENTITY = "Confirm the board record belongs to this provider.";
/** An identity issue already says the record may not be the provider's. */
const IDENTITY_OUTCOME: string = "possible-match";
const ATTACHMENT_ISSUES: Record<Attachment["result"], string> = {
  clear: "Verification clear",
  concern: "Review required",
  "missing-info": "Missing information — review required",
  error: "Verification error — review required",
};

/**
 * The problems in one board result, most fundamental first: whether the record
 * is the provider's, whether the license is valid, then board actions. Each
 * source caveat is attached to the issue it explains, so nothing is said twice.
 */
export function licenseIssues({
  result,
  assessment,
  attachments,
  licenseLimitations,
}: {
  result: VerificationResult;
  assessment?: VerificationAssessmentItem;
  attachments: Attachment[];
  licenseLimitations: string[];
}): LicenseIssue[] {
  const facts = result.facts;
  const issues: LicenseIssue[] = [];
  const identityTitle = IDENTITY_ISSUES[result.identityMatch];
  const identity: LicenseIssue | null = identityTitle
    ? { title: identityTitle, details: [] }
    : null;
  if (identity) {
    issues.push(identity);
  }
  const outcome = OUTCOME_ISSUES[result.outcome];
  if (outcome && !(identity && result.outcome === IDENTITY_OUTCOME)) {
    issues.push({ title: outcome.title, details: [...outcome.details] });
  }
  const statusIssues = licenseStatusIssues({ facts, result });
  // The source's caveats explain the most basic issue: the license status,
  // else the identity. Only without either are they their own issue.
  const explained = statusIssues[0] ?? identity;
  if (licenseLimitations.length && explained) {
    explained.details.push(...licenseLimitations);
  } else if (licenseLimitations.length) {
    statusIssues.push({
      title: "Needs confirmation",
      details: licenseLimitations,
    });
  }
  // An identity issue the source did not explain still says what to do.
  if (identity && !identity.details.length) {
    identity.details.push(CONFIRM_IDENTITY);
  }
  issues.push(...statusIssues);
  issues.push(
    ...(assessment?.disciplinary
      ? boardRecordIssues(assessment.disciplinary)
      : disciplinaryScreeningIssues(facts)),
  );
  // A review can be required for reasons with no specific issue above, such as
  // a saved credential whose dates differ from the board's.
  if (!issues.length && facts.requiresReview === true) {
    issues.push({
      title: "Review required",
      details: assessment?.description ? [assessment.description] : [],
    });
  }
  const concern = attachments.find(
    (attachment) => attachment.result !== "clear",
  );
  if (!issues.length && concern) {
    issues.push({
      title: ATTACHMENT_ISSUES[concern.result],
      details: concern.note ? [concern.note] : [],
    });
  }
  return issues;
}

function licenseStatusIssues({
  facts,
  result,
}: {
  facts: Facts;
  result: VerificationResult;
}): LicenseIssue[] {
  const status = text(facts.status);
  const boardStatus = text(facts.statusRaw);
  const boardStatusDetail = boardStatus ? [`Board status: ${boardStatus}`] : [];
  const registeredThrough = text(facts.registrationExpiresAt);
  const expiry = registeredThrough ?? text(facts.expiresAt);
  const issues: LicenseIssue[] = [];
  if (
    status === EXPIRED_LICENSE_STATUS ||
    (expiry && isBeforeObservation(expiry, result.observedAt))
  ) {
    issues.push({
      title: "License expired",
      details: [
        ...(expiry
          ? [
              `${registeredThrough ? "Registered through" : "Expired"} ${formatDate(expiry)}`,
            ]
          : []),
        ...boardStatusDetail,
      ],
    });
  } else if (status && INVALID_LICENSE_STATUSES.has(status)) {
    issues.push({ title: `License ${status}`, details: boardStatusDetail });
  } else if (status && UNCONFIRMED_LICENSE_STATUSES.has(status)) {
    issues.push({
      title: "License status not confirmed",
      details: boardStatusDetail,
    });
  }
  const secondaryStatus = text(facts.secondaryStatus);
  if (secondaryStatus) {
    issues.push({
      title: "Additional board status",
      details: [secondaryStatus],
    });
  }
  return issues;
}

function boardRecordIssues(disciplinary: DisciplinaryAssessment) {
  const issues: LicenseIssue[] = disciplinary.records.map((record) => ({
    title: record.label,
    details: [
      record.effectiveDate
        ? `Effective ${formatDate(record.effectiveDate)}`
        : "",
      record.action ?? "",
      ...(record.identityMatch === "matched" ? [] : [record.identityLabel]),
    ].filter(Boolean),
  }));
  if (disciplinary.limitations.length) {
    issues.push({
      title: disciplinary.records.length
        ? "Some board record searches did not complete"
        : disciplinary.label,
      details: disciplinary.limitations,
    });
  }
  return issues;
}

function disciplinaryScreeningIssues(facts: Facts): LicenseIssue[] {
  if (facts.hasPublicActions === true) {
    return [
      {
        title: "Public board actions reported",
        details: ["Review the source evidence for the listed actions."],
      },
    ];
  }
  if (facts.disciplinaryScreening === "findings") {
    return [{ title: "Disciplinary findings reported", details: [] }];
  }
  if (facts.disciplinaryScreening === "incomplete") {
    return [
      {
        title: "Disciplinary screening incomplete",
        details: stringList(facts.disciplinaryLimitations),
      },
    ];
  }
  return [];
}

/** Expiry dates are New York calendar days, matching the API's assessment. */
function isBeforeObservation(date: string, observedAt: string) {
  try {
    const observed = Temporal.Instant.from(observedAt)
      .toZonedDateTimeISO(TimeZone.AmericaNewYork)
      .toPlainDate();
    return (
      Temporal.PlainDate.compare(Temporal.PlainDate.from(date), observed) < 0
    );
  } catch {
    return false;
  }
}

function text(value: unknown) {
  return typeof value === "string" && value ? value : null;
}

export function stringList(value: unknown) {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === "string");
}
